export type AssetAccess = Readonly<{ kind: 'public' }> | Readonly<{ kind: 'private'; scope: string }>;

/** Content identity never contains a transport URL or bearer credential. */
export interface AssetDescriptor {
  readonly id: string;
  readonly revision: string;
  readonly sha256: string;
  readonly byteLength: number;
  readonly mimeType: string;
  readonly dependencies: readonly AssetDescriptor[];
  readonly access: AssetAccess;
}

export interface AssetLocation {
  readonly url: string;
  /** Epoch milliseconds. A cached copy does not extend this authorization. */
  readonly expiresAt?: number;
}

export interface AssetResolver {
  resolve(descriptor: AssetDescriptor, signal: AbortSignal): Promise<AssetLocation>;
}

export interface AssetLease {
  readBytes(): Promise<ArrayBuffer>;
  readonly mimeType: string;
  objectUrl(): string;
  release(): void;
}

export interface AssetRequestOptions {
  readonly signal?: AbortSignal;
  /** Higher priorities start first; equal priorities preserve arrival order. */
  readonly priority?: number;
}

export interface AssetAcquisition {
  acquire(descriptor: AssetDescriptor, resolver: AssetResolver, options?: AssetRequestOptions): Promise<AssetLease>;
  acquireUnverified(location: AssetLocation, options?: AssetRequestOptions): Promise<AssetLease>;
  purgePrivateScope(scope?: string): Promise<void>;
  dispose(): void;
}

export interface AssetRecord {
  readonly key: string;
  readonly byteLength: number;
  readonly lastAccess: number;
  readonly scope: string | null;
}

/** Implementations store bytes, never decoded GPU/audio resources. */
export interface AssetStore {
  read(key: string): Promise<ArrayBuffer | null>;
  write(record: AssetRecord, bytes: ArrayBuffer): Promise<void>;
  remove(key: string): Promise<void>;
  records(): Promise<readonly AssetRecord[]>;
  touch(key: string, at: number): Promise<void>;
  estimate?(): Promise<{ quota?: number; usage?: number }>;
  withExclusiveLock?<T>(operation: () => Promise<T>): Promise<T>;
}

export function assetKey(descriptor: AssetDescriptor): string {
  return JSON.stringify([descriptor.access.kind === 'private' ? descriptor.access.scope : null,
    descriptor.sha256, descriptor.byteLength, descriptor.mimeType]);
}

export function validateAssetDescriptor(value: unknown, ancestors = new Set<unknown>()): asserts value is AssetDescriptor {
  if (!value || typeof value !== 'object' || ancestors.has(value) || ancestors.size > 32) {
    throw new Error('Invalid or cyclic asset descriptor.');
  }
  const descriptor = value as AssetDescriptor;
  if (typeof descriptor.id !== 'string' || !descriptor.id || descriptor.id.length > 512
    || typeof descriptor.revision !== 'string' || !descriptor.revision || descriptor.revision.length > 256
    || typeof descriptor.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(descriptor.sha256)
    || !Number.isSafeInteger(descriptor.byteLength) || descriptor.byteLength < 0
    || typeof descriptor.mimeType !== 'string' || !/^[\w.+-]+\/[\w.+-]+$/.test(descriptor.mimeType)
    || !Array.isArray(descriptor.dependencies) || descriptor.dependencies.length > 256
    || !descriptor.access || !['public', 'private'].includes(descriptor.access.kind)
    || (descriptor.access.kind === 'private' && (typeof descriptor.access.scope !== 'string'
      || !descriptor.access.scope || descriptor.access.scope.length > 512))) {
    throw new Error('Invalid asset descriptor.');
  }
  ancestors.add(value);
  for (const dependency of descriptor.dependencies) validateAssetDescriptor(dependency, ancestors);
  ancestors.delete(value);
}
